import { useMemo, useState } from 'react'

interface UsePaginationProps<T> {
  data: Array<T>
  pageSize?: number
}

interface UsePaginationReturn<T> {
  page: number
  pageSize: number
  pageCount: number
  rows: Array<T>
  setPage: (page: number) => void
  goToFirstPage: () => void
}

export function usePagination<T>({
  data,
  pageSize = 10,
}: UsePaginationProps<T>): UsePaginationReturn<T> {
  const [page, setPage] = useState(1)

  const pageCount = useMemo(() => {
    return Math.max(1, Math.ceil(data.length / pageSize))
  }, [data.length, pageSize])

  const rows = useMemo(() => {
    return data.slice((page - 1) * pageSize, page * pageSize)
  }, [data, page, pageSize])

  const goToFirstPage = () => {
    setPage(1)
  }

  // Reset to first page if current page is beyond available pages
  useMemo(() => {
    if (page > pageCount) {
      setPage(1)
    }
  }, [page, pageCount])

  return {
    page,
    pageSize,
    pageCount,
    rows,
    setPage,
    goToFirstPage,
  }
}
